/**
 * App-level names for the API's per-DTO enums.
 *
 * The OpenAPI regen stopped emitting standalone enum models and now inlines an
 * enum on every DTO that references it, so one concept (`BookingStatus`,
 * `TimeZoneDto`, …) is generated several times under several names whose member
 * sets can drift apart. Importing one of those per-DTO names into shared code
 * couples a component to whichever DTO happened to be convenient, and a chip
 * keyed off the wrong twin renders blank rather than failing to compile.
 *
 * This module is the one place the app names those concepts. Each alias points
 * at a deliberately chosen source — normally the read DTO, which carries the
 * widest member set — and `enum-aliases.test.ts` pins the members so the next
 * regen fails loudly here instead of silently at a call site.
 *
 * Import these from `@/lib/api`; never reach for a `<parentDto><Field>` name
 * directly outside this file.
 */

import { AvailabilityDtoType } from "./generated/fleet/models/availabilityDtoType";
import { BatteryStatusDtoCharging } from "./generated/fleet/models/batteryStatusDtoCharging";
import { BookingDtoBookingType } from "./generated/fleet/models/bookingDtoBookingType";
import { BookingEventDtoAction } from "./generated/fleet/models/bookingEventDtoAction";
import { BookingEventDtoSubject } from "./generated/fleet/models/bookingEventDtoSubject";
import { BookingReferenceDtoStatus } from "./generated/fleet/models/bookingReferenceDtoStatus";
import { CardAssignmentDtoStatus } from "./generated/fleet/models/cardAssignmentDtoStatus";
import { CardDtoProvider } from "./generated/fleet/models/cardDtoProvider";
import { CardDtoStatus } from "./generated/fleet/models/cardDtoStatus";
import { CardDtoType } from "./generated/fleet/models/cardDtoType";
import { CardEventDtoAction } from "./generated/fleet/models/cardEventDtoAction";
import { CardEventDtoSubject } from "./generated/fleet/models/cardEventDtoSubject";
import { ChangeLockStatusDtoStatus } from "./generated/fleet/models/changeLockStatusDtoStatus";
import { ChargingCostDtoType } from "./generated/fleet/models/chargingCostDtoType";
import { ChargingSessionReferenceDtoStatus } from "./generated/fleet/models/chargingSessionReferenceDtoStatus";
import { ChargingStationDtoConnectorType } from "./generated/fleet/models/chargingStationDtoConnectorType";
import { ChargingStationDtoPowerType } from "./generated/fleet/models/chargingStationDtoPowerType";
import { CleaningDtoStatus } from "./generated/fleet/models/cleaningDtoStatus";
import { CleaningDtoType } from "./generated/fleet/models/cleaningDtoType";
import { CleaningDtoUrgency } from "./generated/fleet/models/cleaningDtoUrgency";
import { ClientMembershipDtoFormula } from "./generated/fleet/models/clientMembershipDtoFormula";
import { ClientEventDtoAction } from "./generated/fleet/models/clientEventDtoAction";
import { ClientEventDtoSubject } from "./generated/fleet/models/clientEventDtoSubject";
import { ClientMembershipDtoRole } from "./generated/fleet/models/clientMembershipDtoRole";
import { ConflictResolutionDtoMode } from "./generated/fleet/models/conflictResolutionDtoMode";
import { DamageDtoPhase } from "./generated/fleet/models/damageDtoPhase";
import { DamageDtoStatus } from "./generated/fleet/models/damageDtoStatus";
import { EndVehicleUsageRequestDtoTelematicsInteraction } from "./generated/fleet/models/endVehicleUsageRequestDtoTelematicsInteraction";
import { OnboardingDriverLicenseDtoLicenseType } from "./generated/fleet/models/onboardingDriverLicenseDtoLicenseType";
import { OnboardingDtoStatus } from "./generated/fleet/models/onboardingDtoStatus";
import { PeriodDtoTimeZone } from "./generated/fleet/models/periodDtoTimeZone";
import { PinEventDtoAction } from "./generated/fleet/models/pinEventDtoAction";
import { PinEventDtoSubject } from "./generated/fleet/models/pinEventDtoSubject";
import { RecurringScheduleDtoRecurringType } from "./generated/fleet/models/recurringScheduleDtoRecurringType";
import { SearchVehiclesRequestDtoAccessMode } from "./generated/fleet/models/searchVehiclesRequestDtoAccessMode";
import { StatusActionDtoType } from "./generated/fleet/models/statusActionDtoType";
import { SystemStatusDtoSeverity } from "./generated/fleet/models/systemStatusDtoSeverity";
import { VehicleAccessGrantDtoLevel } from "./generated/fleet/models/vehicleAccessGrantDtoLevel";
import { VehicleAuditEventDtoAction } from "./generated/fleet/models/vehicleAuditEventDtoAction";
import { VehicleAuditEventDtoOutcome } from "./generated/fleet/models/vehicleAuditEventDtoOutcome";
import { VehicleAuditEventDtoSubject } from "./generated/fleet/models/vehicleAuditEventDtoSubject";
import { VehicleDetailDtoOperationalStatus } from "./generated/fleet/models/vehicleDetailDtoOperationalStatus";
import { VehicleFilterPermissionSettingDtoPermission } from "./generated/fleet/models/vehicleFilterPermissionSettingDtoPermission";
import { TelematicsStatusDtoKeyFobStatus } from "./generated/fleet/models/telematicsStatusDtoKeyFobStatus";
import { VehicleLockStatusDtoImmobilizerStatus } from "./generated/fleet/models/vehicleLockStatusDtoImmobilizerStatus";
import { VehicleSuspensionDtoKind } from "./generated/fleet/models/vehicleSuspensionDtoKind";
import { VehicleSuspensionDtoLiftReason } from "./generated/fleet/models/vehicleSuspensionDtoLiftReason";
import { VehicleSuspensionDtoReason } from "./generated/fleet/models/vehicleSuspensionDtoReason";
import { VehicleUsagePermissionSettingDtoPermission } from "./generated/fleet/models/vehicleUsagePermissionSettingDtoPermission";
import { VehicleUsageReferenceDtoStatus } from "./generated/fleet/models/vehicleUsageReferenceDtoStatus";

/** Only surface that declares it. */
export type AccessMode = SearchVehiclesRequestDtoAccessMode;

export const AccessMode = SearchVehiclesRequestDtoAccessMode;

/** System-status action type. */
export type ActionType = StatusActionDtoType;

export const ActionType = StatusActionDtoType;

/** Read DTO carries every type. */
export type AvailabilityTypeDto = AvailabilityDtoType;

export const AvailabilityTypeDto = AvailabilityDtoType;

/** Read DTO. */
export type BookingEventActionDto = BookingEventDtoAction;

export const BookingEventActionDto = BookingEventDtoAction;

/** Read DTO. */
export type BookingEventSubjectDto = BookingEventDtoSubject;

export const BookingEventSubjectDto = BookingEventDtoSubject;

/** Widest: the reference DTO carries every state, the request DTO only settable ones. */
export type BookingStatus = BookingReferenceDtoStatus;

export const BookingStatus = BookingReferenceDtoStatus;

/** Alias of BookingStatus; both names were in use. */
export type BookingStatusDto = BookingReferenceDtoStatus;

export const BookingStatusDto = BookingReferenceDtoStatus;

/** Read DTO. */
export type BookingType = BookingDtoBookingType;

export const BookingType = BookingDtoBookingType;

/** Read DTO. */
export type CardAssignmentStatusDto = CardAssignmentDtoStatus;

export const CardAssignmentStatusDto = CardAssignmentDtoStatus;

/** Only declaration. */
export type CardEventActionDto = CardEventDtoAction;

export const CardEventActionDto = CardEventDtoAction;

/** Only declaration. */
export type CardEventSubjectDto = CardEventDtoSubject;

export const CardEventSubjectDto = CardEventDtoSubject;

/** Read DTO. */
export type CardPhysicalTypeDto = CardDtoType;

export const CardPhysicalTypeDto = CardDtoType;

/** Read DTO. */
export type CardProviderDto = CardDtoProvider;

export const CardProviderDto = CardDtoProvider;

/** Read DTO. */
export type CardStatusDto = CardDtoStatus;

export const CardStatusDto = CardDtoStatus;

/** Read DTO. */
export type ChargingCostTypeDto = ChargingCostDtoType;

export const ChargingCostTypeDto = ChargingCostDtoType;

/** Same member set as the station/input variants; one concept, one alias. */
export type ChargingProviderDto = CardDtoProvider;

export const ChargingProviderDto = CardDtoProvider;

/** Read DTO. */
export type ChargingSessionConnectorTypeDto = ChargingStationDtoConnectorType;

export const ChargingSessionConnectorTypeDto = ChargingStationDtoConnectorType;

/** Read DTO. */
export type ChargingSessionPowerTypeDto = ChargingStationDtoPowerType;

export const ChargingSessionPowerTypeDto = ChargingStationDtoPowerType;

/** Read DTO. */
export type ChargingSessionStatusDto = ChargingSessionReferenceDtoStatus;

export const ChargingSessionStatusDto = ChargingSessionReferenceDtoStatus;

/** Consumed as BatteryStatusDto.charging. */
export type ChargingStatusDto = BatteryStatusDtoCharging;

export const ChargingStatusDto = BatteryStatusDtoCharging;

/** Read DTO. */
export type CleaningTypeDto = CleaningDtoType;

export const CleaningTypeDto = CleaningDtoType;

/** Read DTO. */
export type CleaningUrgencyDto = CleaningDtoUrgency;

export const CleaningUrgencyDto = CleaningDtoUrgency;

/** Read DTO. */
export type ClientContractFormulaDto = ClientMembershipDtoFormula;

export const ClientContractFormulaDto = ClientMembershipDtoFormula;

/** Read DTO; removeAvailabilities* and the response item share the members. */
export type ConflictResolutionModeDto = ConflictResolutionDtoMode;

export const ConflictResolutionModeDto = ConflictResolutionDtoMode;

/** Read DTO. */
export type DamageStatusDto = DamageDtoStatus;

export const DamageStatusDto = DamageDtoStatus;

/** Booking events; card and PIN events now have their own enums. */
export type EventActionDto = BookingEventDtoAction;

export const EventActionDto = BookingEventDtoAction;

/** Booking events; see EventActionDto. */
export type EventSubjectDto = BookingEventDtoSubject;

export const EventSubjectDto = BookingEventDtoSubject;

/** Consumed by the cleaning-request mapper. */
export type IssueStatusDto = CleaningDtoStatus;

export const IssueStatusDto = CleaningDtoStatus;

/** Read DTO. */
export type LicenseTypeDto = OnboardingDriverLicenseDtoLicenseType;

export const LicenseTypeDto = OnboardingDriverLicenseDtoLicenseType;

/** Sent as the changeLock/changeImmobilizer request status. */
export type LockStatusDto = ChangeLockStatusDtoStatus;

export const LockStatusDto = ChangeLockStatusDtoStatus;

/**
 * Where the vehicle's key is, as far as the device can tell.
 *
 * Four of the six members are flavours of "we do not know" — NOT_SUPPORTED for a
 * provider that cannot report it at all (Flespi answers this unconditionally),
 * plus TIMEOUT, ERROR_FETCH and UNKNOWN. Only IN_HOLDER and NOT_IN_HOLDER are
 * statements about the key, and treating the rest as either would be inventing a
 * fact the fleet cannot supply.
 */
export type KeyFobStatus = TelematicsStatusDtoKeyFobStatus;

export const KeyFobStatus = TelematicsStatusDtoKeyFobStatus;

/**
 * Read DTO — the widest member set: it is the only one of these three carrying
 * `NOT_APPLICABLE`, which is how a vehicle with no immobilizer at all reports.
 */
export type ImmobilizerStatus = VehicleLockStatusDtoImmobilizerStatus;

export const ImmobilizerStatus = VehicleLockStatusDtoImmobilizerStatus;

/** Read DTO. */
export type MembershipRoleDto = ClientMembershipDtoRole;

export const MembershipRoleDto = ClientMembershipDtoRole;

/** Only declaration. */
export type OnboardingStatusDto = OnboardingDtoStatus;

export const OnboardingStatusDto = OnboardingDtoStatus;

/** Only declaration. */
export type PinEventActionDto = PinEventDtoAction;

export const PinEventActionDto = PinEventDtoAction;

/** Only declaration. */
export type PinEventSubjectDto = PinEventDtoSubject;

export const PinEventSubjectDto = PinEventDtoSubject;

/** Only declaration. */
export type RecurringTypeDto = RecurringScheduleDtoRecurringType;

export const RecurringTypeDto = RecurringScheduleDtoRecurringType;

/** Read DTO. */
export type ReportingPhaseDto = DamageDtoPhase;

export const ReportingPhaseDto = DamageDtoPhase;

/** Read DTO. */
export type SystemStatusSeverity = SystemStatusDtoSeverity;

export const SystemStatusSeverity = SystemStatusDtoSeverity;

/** Start/end/end-private share the members. */
export type TelematicsInteractionDto =
	EndVehicleUsageRequestDtoTelematicsInteraction;

export const TelematicsInteractionDto =
	EndVehicleUsageRequestDtoTelematicsInteraction;

/** Alias of TelematicsInteractionDto; both names were in use. */
export type FleetVehicleTelematicsInteractionDto =
	EndVehicleUsageRequestDtoTelematicsInteraction;

export const FleetVehicleTelematicsInteractionDto =
	EndVehicleUsageRequestDtoTelematicsInteraction;

/** Every period-bearing request uses the same set. */
export type TimeZoneDto = PeriodDtoTimeZone;

export const TimeZoneDto = PeriodDtoTimeZone;

/**
 * Permissions a *booking filter* can require. Distinct from VehicleUsagePermissionDto
 * on purpose: the filter set carries KIDS as well as PETS and ABROAD, so the two are
 * overlapping concepts rather than one, and collapsing them would silently narrow
 * whichever call site got the wrong twin.
 */
export type VehicleFilterPermissionDto =
	VehicleFilterPermissionSettingDtoPermission;

export const VehicleFilterPermissionDto =
	VehicleFilterPermissionSettingDtoPermission;

/**
 * Read DTO. The PUT request and the removal result carry the same two members;
 * the read side is what the access list renders.
 */
export type VehicleAccessLevelDto = VehicleAccessGrantDtoLevel;

export const VehicleAccessLevelDto = VehicleAccessGrantDtoLevel;

/** Read DTO for the detail page. */
export type VehicleOperationalStatus = VehicleDetailDtoOperationalStatus;

export const VehicleOperationalStatus = VehicleDetailDtoOperationalStatus;

/** Only declaration. */
export type VehicleUsagePermissionDto =
	VehicleUsagePermissionSettingDtoPermission;

export const VehicleUsagePermissionDto =
	VehicleUsagePermissionSettingDtoPermission;

/** Only declaration. */
export type VehicleUsageStatusDto = VehicleUsageReferenceDtoStatus;

export const VehicleUsageStatusDto = VehicleUsageReferenceDtoStatus;

/** Only declaration: what a Client activity entry is about. */
export type ClientActivitySubjectDto = ClientEventDtoSubject;

export const ClientActivitySubjectDto = ClientEventDtoSubject;

/** Only declaration. */
export type ClientActivityActionDto = ClientEventDtoAction;

export const ClientActivityActionDto = ClientEventDtoAction;

/**
 * Only declaration: what a Vehicle activity entry from the vehicle events
 * endpoint is about. Not BookingEventSubjectDto, which is the booking side.
 */
export type VehicleActivitySubjectDto = VehicleAuditEventDtoSubject;

export const VehicleActivitySubjectDto = VehicleAuditEventDtoSubject;

/** Only declaration. */
export type VehicleActivityActionDto = VehicleAuditEventDtoAction;

export const VehicleActivityActionDto = VehicleAuditEventDtoAction;

/** Only declaration. */
export type VehicleActivityOutcomeDto = VehicleAuditEventDtoOutcome;

export const VehicleActivityOutcomeDto = VehicleAuditEventDtoOutcome;

/**
 * Read DTO: how far a Suspension reaches. Twin of the request DTO's kind.
 */
export type VehicleSuspensionKindDto = VehicleSuspensionDtoKind;

export const VehicleSuspensionKindDto = VehicleSuspensionDtoKind;

/** Read DTO: the Reason category. Twin of the request DTO's reason. */
export type VehicleSuspensionReasonDto = VehicleSuspensionDtoReason;

export const VehicleSuspensionReasonDto = VehicleSuspensionDtoReason;

/** Read DTO: the Lift category. Twin of the lift request DTO's reason. */
export type VehicleSuspensionLiftReasonDto = VehicleSuspensionDtoLiftReason;

export const VehicleSuspensionLiftReasonDto = VehicleSuspensionDtoLiftReason;
